'use client'
import { type FC, useEffect, useState } from 'react'
import { AddressItemCard } from './address-item-card/AddressItemCard'
import { CurrentLocationAddressPlaceholder } from './current-location-address-placeholder/CurrentLocationAddressPlaceholder'
import { getAddressInCurrentLocation, getNormalizedAddressList } from './helpers'
import { ShareLocationItem } from './share-location-item/ShareLocationItem'
import { useAuth } from '@/tools/auth'
import { useErrorHandling } from '@/tools/o11y/client'
import { LoaderWrapper } from '@/tools/ui'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import type { UserAddress } from '@/user/address-book/entities'
import { GeolocationPermission } from '@/user/address-book/types'
import { getUserAddresses } from '@/user/address-book/use-cases'

export const AddressList: FC = () => {
  const { execWithErrorTracking } = useErrorHandling()
  const { browserLocation, verifiedBrowserAddress } = useAddressBookContext()
  const { isLoggedIn } = useAuth()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [userAddressList, setUserAddressList] = useState<UserAddress[]>([])

  const normalizedAddressList = getNormalizedAddressList(
    userAddressList,
    browserLocation?.coordinates
  )
  const hasAddressMatchCurrentLocation = Boolean(getAddressInCurrentLocation(normalizedAddressList))
  const shouldShowCurrentLocationPlaceholder =
    verifiedBrowserAddress && !hasAddressMatchCurrentLocation

  useEffect(() => {
    execWithErrorTracking(async () => {
      setIsLoading(true)
      if (isLoggedIn) {
        try {
          const addresses = await getUserAddresses()
          setUserAddressList(addresses)
        } finally {
          setIsLoading(false)
        }
      } else {
        setIsLoading(false)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, browserLocation])

  return (
    <LoaderWrapper loading={isLoading}>
      <>
        {browserLocation?.geolocationPermission !== GeolocationPermission.UNSUPPORTED &&
          browserLocation?.geolocationPermission !== GeolocationPermission.GRANTED && (
            <ShareLocationItem data-testid="share-location-item" />
          )}
        <section>
          {shouldShowCurrentLocationPlaceholder && (
            <CurrentLocationAddressPlaceholder
              currentAddress={verifiedBrowserAddress}
              data-testid="current-location-placeholder"
            />
          )}

          {normalizedAddressList?.length > 0 &&
            normalizedAddressList.map((addressItem, index) => (
              <AddressItemCard
                key={addressItem.id}
                addressItem={addressItem}
                isLastItem={index === userAddressList?.length - 1}
              />
            ))}
        </section>
      </>
    </LoaderWrapper>
  )
}
